import React from "react";
import {
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
} from "@mui/material";

// Delay scale. Values are mirrored in tokens.json under shared.durations
// (`tooltip-enter: 500ms`, `tooltip-leave: 150ms`); CSS consumers reach
// them via `var(--duration-tooltip-*)`. MUI takes numbers, so we duplicate
// the values here. If you change one, change the other.
const ENTER_DELAY_MS = 500;
const LEAVE_DELAY_MS = 150;
const DELIBERATE_DELAY_MS = 1000;
const CARD_DELAY_MS = 1500;

interface TooltipProps {
  /**
   * Tooltip body. `null`/`undefined`/`""` causes MUI to suppress the popper
   * (its built-in empty-title behavior) without unmounting — important for
   * lazy-fetch callers: hover handlers stay bound while the title is still
   * null, so when the fetch resolves the next render's non-empty title pops
   * the tooltip in place rather than requiring the user to mouse out and
   * back in.
   */
  title: React.ReactNode | null | undefined;
  children: React.ReactElement;
  placement?: MuiTooltipProps["placement"];
  /** Rem-based cap. Defaults to 22.5rem (~360px), wide enough for a paragraph. */
  maxWidth?: string;
  /** Anchor the tooltip to the cursor rather than the trigger element. Useful for wide rows where the trigger is much larger than the tooltip. */
  followCursor?: boolean;
  /**
   * `"default"` wraps `title` in the standard text pill — bordered surface,
   * padding, `white-space: pre-line`. `"card"` strips that chrome so the
   * caller's `title` JSX renders as a free-form card (its own surface,
   * border, shadow) AND uses long deliberate delays (1500ms) because the
   * body is dense. Use for rich React components (e.g. UnitInfoCard).
   */
  variant?: "default" | "card";
  /**
   * Skip the standard enter delay (sets enter/enterNext to 0). Use when the
   * trigger has already proven hover intent — e.g. TooltipButton, which
   * mounts the tooltip via a click-armed open state.
   */
  instant?: boolean;
  /**
   * Long-press style delay (1000ms enter + enterNextDelay) for dense rows
   * where rapid mouse-overs would spam the tooltip. Use sparingly; only
   * appropriate when the tooltip body is long or the row is in a scanning
   * context.
   */
  deliberate?: boolean;
  /** Controlled open state. Pass when the caller manages visibility (e.g. error tooltips that show on validation failure). */
  open?: boolean;
  /** Fired when MUI's hover/focus heuristics request the tooltip open. Paired with `open` for controlled callers. */
  onOpen?: () => void;
  /** Fired when MUI's hover/focus heuristics request the tooltip close. Paired with `open` for controlled callers. */
  onClose?: () => void;
  /** Enable hover-into-tooltip interaction. Off by default; on means the user can mouse into the popper without it closing. */
  interactive?: boolean;
  /** Suppress mouse-hover capture entirely. Use when the parent component already manages hover semantics and the tooltip should only respond to focus or controlled open state. */
  disableHoverListener?: boolean;
  /** Suppress focus-driven open. Pair with `disableHoverListener` when the trigger element is non-interactive and should never receive a tooltip. */
  disableFocusListener?: boolean;
  /** Override the arrow. Defaults on for `default`, off for `card`. Pass `true`
   * to give a card a connecting arrow (colored to the card surface). */
  arrow?: boolean;
}

export const Tooltip = ({
  title,
  children,
  placement = "top",
  maxWidth = "22.5rem",
  followCursor = false,
  variant = "default",
  instant = false,
  deliberate = false,
  open,
  onOpen,
  onClose,
  interactive = false,
  disableHoverListener = false,
  disableFocusListener = false,
  arrow,
}: TooltipProps) => {
  const showArrow = arrow ?? variant !== "card";
  // The arrow tip must match the surface it points at: the card variant paints
  // its own `bg-bg-subtle` surface, the default pill uses `bg`.
  const arrowColor =
    variant === "card" ? "var(--color-bg-subtle)" : "var(--color-bg)";
  const enterDelay = instant
    ? 0
    : variant === "card"
      ? CARD_DELAY_MS
      : deliberate
        ? DELIBERATE_DELAY_MS
        : ENTER_DELAY_MS;
  const enterNextDelay =
    instant || variant === "card" || deliberate ? enterDelay : undefined;
  const leaveDelay = instant ? 0 : variant === "card" ? 0 : LEAVE_DELAY_MS;

  const tooltipSx =
    variant === "card"
      ? {
          backgroundColor: "transparent",
          color: "inherit",
          border: 0,
          borderRadius: 0,
          padding: 0,
          maxWidth: "none",
          boxShadow: "none",
          // Popper shifts a card into view but never shrinks it, so cap it to
          // the viewport. This bounds the box only — MUI computes
          // `pointer-events: none` on this popper, so the scrollbar cannot be
          // operated and overflowing content stays unreachable. A card whose
          // body must be readable in full needs a click-opened panel instead
          // (see UnitInfoCard in DetailsOverlay), not a taller tooltip.
          maxHeight: "calc(100vh - var(--spacing-3xl))",
          overflowY: "auto" as const,
          overflowX: "hidden" as const,
          overscrollBehavior: "contain" as const,
        }
      : {
          backgroundColor: "var(--color-bg)",
          color: "var(--color-fg)",
          border: "var(--size-border-hairline) solid var(--color-border)",
          borderRadius: "var(--radius-app)",
          fontSize: "var(--font-size-small)",
          lineHeight: 1.4,
          padding: "var(--spacing-sm) var(--spacing-md)",
          maxWidth,
          whiteSpace: "pre-line" as const,
        };

  return (
    <MuiTooltip
      title={title ?? ""}
      placement={placement}
      enterDelay={enterDelay}
      enterNextDelay={enterNextDelay}
      leaveDelay={leaveDelay}
      arrow={showArrow}
      disableInteractive={!interactive}
      followCursor={followCursor}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      disableHoverListener={disableHoverListener}
      disableFocusListener={disableFocusListener}
      slotProps={{
        popper: { sx: { zIndex: "var(--z-tooltip)" } },
        tooltip: { sx: tooltipSx },
        arrow: {
          sx: {
            color: arrowColor,
            "&::before": {
              border: "var(--size-border-hairline) solid var(--color-border)",
            },
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
};
